'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/api';
import { Loader2 } from 'lucide-react';
import { sanitizeErrorMessage } from '@/lib/error-utils';
import { pickRegistrationLocale } from '@/lib/registrationLocale';

interface GoogleSignInButtonProps {
  flow: 'login' | 'register';
  /**
   * The "or" separator makes sense under a form, but not when this button is
   * itself one of two top-level choices.
   */
  showDivider?: boolean;
  /**
   * Invite-code carried over from the landing page. It rides inside the
   * encrypted OAuth state, so the organization provisioned in the callback gets
   * the invited plan just like a password signup does.
   */
  invitationCode?: string;
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function GoogleSignInButton({ flow, showDivider = true, invitationCode }: GoogleSignInButtonProps) {
  const { t, i18n } = useTranslation(['auth']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    try {
      setError(null);
      setLoading(true);

      const authUrl = await authService.getGoogleAuthUrl({
        flow,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // i18n.language can be "en-US"; the backend validates against a fixed
        // list, so normalize before sending.
        locale: pickRegistrationLocale(i18n.language),
        invitationCode: invitationCode || undefined,
      });

      window.location.assign(authUrl);
    } catch (err: any) {
      // render_error returns `error` as a plain string, so the old
      // `data.error.message` read was always undefined and users saw axios's
      // "Request failed with status code 422" instead of the real reason.
      setError(sanitizeErrorMessage(err, t('auth:google.error')));
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      {showDivider && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-2 text-gray-500">{t('auth:google.divider')}</span>
          </div>
        </div>
      )}

      {/*
        Styled to match the split-layout sign-up (SL-269) rather than the shared
        Button component, and used on sign-in too so the two pages line up —
        which was the point of that rework.
      */}
      <button
        type="button"
        disabled={loading}
        onClick={handleClick}
        className="flex w-full items-center justify-center gap-3 rounded-md border border-line bg-white px-4 py-2.5 text-sm font-medium text-ink shadow-sm transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-white"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleIcon />}
        {t(`auth:google.${flow === 'register' ? 'continueWithGoogleRegister' : 'continueWithGoogle'}`)}
      </button>

      {error && <p className="text-sm text-red-600 text-center">{error}</p>}
    </div>
  );
}
